import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { FlatfileResults } from '@flatfile/react';
import {
  getFormQuestions,
  selectFormQuestions,
  selectFormsOptions,
} from 'state/Forms/formSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  getBusinessTeams,
  selectBusinessTeamsForDropdown,
} from 'state/BusinessTeams/businessTeamsSlice';
import { selectUser } from 'state/User/userSlice';
import { SelectOrganisationId } from 'state/Organization/organizationSlice';
import {
  getLDUsers,
  selectLDUsersForDropdown,
  getAllUsers,
  selectAllUsersForDropdown,
} from 'state/UsersManagement/usersManagementSlice';
import { importRequests } from 'state/Requests/requestSlice';
import { cleanImportedRequests } from '../helpers/flatfileHelper';
import { flatfileSettings } from '../helpers/flatfileSettings';
import { Form } from 'utils/customTypes';

const useImportRequests = (form?: Form) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const organizationId = useSelector(SelectOrganisationId);
  const forms = useSelector(selectFormsOptions);
  const businessTeams = useSelector(selectBusinessTeamsForDropdown);
  const ldUsers = useSelector(selectLDUsersForDropdown);
  const users = useSelector(selectAllUsersForDropdown);
  const questions = useSelector(selectFormQuestions);

  useEffect(() => {
    dispatch(getBusinessTeams());
    dispatch(getLDUsers());
    dispatch(getAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (form) {
      dispatch(getFormQuestions(form.id!));
    }
  }, [dispatch, form]);

  const isReady = useMemo(
    () =>
      !isEmpty(forms) &&
      !isEmpty(businessTeams) &&
      !isEmpty(ldUsers) &&
      !isEmpty(users),
    [businessTeams, forms, ldUsers, users]
  );

  const settings = useMemo(
    () => flatfileSettings(forms, ldUsers, users, businessTeams, questions),
    [businessTeams, forms, ldUsers, users, questions]
  );

  const cleanUploadedDataAndCreate = async (result: FlatfileResults) => {
    const cleanRequestsData = await cleanImportedRequests(
      result,
      organizationId,
      form?.id || get(forms, '[0].value') || '',
      get(businessTeams, '[0]') || '',
      questions,
      form?.request_type
    );

    try {
      await dispatch(
        importRequests({
          importData: cleanRequestsData,
          userId: currentUser.id || '',
        })
      );
      dispatch(
        showNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            'REQUESTS_LIST_PAGE.TABLE.IMPORT.SUCCESS_SNACKBAR'
          ),
        })
      );
    } catch {
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'REQUESTS_LIST_PAGE.TABLE.IMPORT.ERROR_SNACKBAR_TITLE'
          ),
          notificationMessage: intl.get(
            'REQUESTS_LIST_PAGE.TABLE.IMPORT.ERROR_SNACKBAR_MESSAGE'
          ),
        })
      );
    }
  };

  return {
    currentUser,
    settings,
    isReady,
    onData: cleanUploadedDataAndCreate,
  };
};

export default useImportRequests;
